import React from 'react';

import Highlighter from 'react-highlight-words';

const ORANGE_COLOR = '#ff8000';
const WHITE_COLOR = '';
const LIGHT_ORANGE_COLOR = '#fff2e6';
const LIGHT_GREY_COLOR = '#f4f5f6';
const MEDIUM_GREY_COLOR = '#c3c8cD';
const DARK_GREY_COLOR = '#808b96';
const BLACK_COLOR = '#070a0d';
const DARK_BLUE = '#687a8c';
const LIGHT_RED = '#ffeae5';

export interface OptionType {
  label: string;
  value: string;
  iconUrl?: string;
  __isNew__?: boolean;
}

export type MetaType = {
  context: 'menu' | 'value';
  inputValue: string;
};

export const formatOptionLabel = (option: OptionType, labelMeta: MetaType) => {
  if (option.__isNew__) {
    return option.label;
  }

  return (
    <Highlighter
      searchWords={[labelMeta.inputValue]}
      textToHighlight={option.label}
      autoEscape
      highlightStyle={{ backgroundColor: LIGHT_RED }}
    />
  );
};

export const convertInputSelectValue = (value: string) => {
  return value.toLowerCase().split(' ').join('_');
};

export const getInputSelectOptions = (values: string[]) => {
  return [
    ...values.map(
      (val) =>
        ({
          value: convertInputSelectValue(val),
          label: val,
        } as OptionType),
    ),
  ];
};

export interface CustomStylesProps {
  error?: string;
  loading?: boolean;
  disabled?: boolean;
  isTablet?: boolean;
  isMobile?: boolean;
  isSelected?: boolean;
}

const determineSingleValueTextColor = (disabled: boolean, loading: boolean) => {
  if (!disabled && !loading) {
    return BLACK_COLOR;
  }

  if (disabled) {
    return DARK_BLUE;
  }

  return MEDIUM_GREY_COLOR;
};

export const getDefaultCustomStyles = ({
  error,
  loading = false,
  disabled = false,
  isTablet = false,
  isMobile = false,
  isSelected = false,
}: CustomStylesProps) => {
  return {
    option: (provided: any, state: any) => ({
      background: state.isFocused && !isSelected ? `${LIGHT_GREY_COLOR} !important` : WHITE_COLOR,
      backgroundColor: isSelected ? LIGHT_ORANGE_COLOR : WHITE_COLOR,
      color: BLACK_COLOR,
      padding: '15px 20px',
      cursor: 'pointer',
      fontWeight: 500,
      fontSize: '16px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      background: disabled ? LIGHT_GREY_COLOR : WHITE_COLOR,
      border: error
        ? `2px solid ${ORANGE_COLOR}`
        : disabled
        ? `1px solid ${MEDIUM_GREY_COLOR}`
        : `1px solid ${DARK_GREY_COLOR}`,
      borderRadius:
        state.menuIsOpen && (state.selectProps.isLoading || state.options.length) > 0 ? '5px 5px 0 0' : '5px',
      height: isMobile ? '50px' : '60px',
      outline: 'none',
      boxShadow: 'none',
      '&:hover': {
        cursor: 'pointer',
        border: error ? `2px solid ${ORANGE_COLOR}` : `1px solid ${DARK_GREY_COLOR}`,
      },
      fontSize: '16px',
    }),
    menu: (provided: any) => ({
      ...provided,
      marginTop: 0,
      border: 'none',
      borderRadius: '0 0 5px 5px',
      borderTop: 0,
      boxShadow:
        '0px 2.78659px 5.31834px rgba(44, 61, 80, 0.07), 0px 5.66679px 11.3396px rgba(44, 61, 80, 0.0589391),\n' +
        '  0px 9.71848px 18.7755px rgba(44, 61, 80, 0.0502519), 0px 17.4254px 29.0776px rgba(44, 61, 80, 0.0423627),\n' +
        '  0px 35.8633px 45.7244px rgba(44, 61, 80, 0.0340255), 0px 100px 80px rgba(44, 61, 80, 0.0232689)',
    }),
    menuList: (provided: any) => ({
      ...provided,
      paddingTop: 0,
      borderRadius: '0 5px',
      '&::-webkit-scrollbar': {
        width: '12px',
      },
      '&::-webkit-scrollbar-thumb': {
        border: '4px solid rgba(0, 0, 0, 0)',
        backgroundClip: 'padding-box',
        borderRadius: '10px',
        backgroundColor: '#C3C8CD',
      },
      zIndex: '999000',
      maxHeight: '230px',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      paddingLeft: isTablet ? '10px' : '15px',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: '0 16px 0 2px',
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      color: determineSingleValueTextColor(disabled, loading),
      fontWeight: 500,
      display: state.selectProps.menuIsOpen ? 'none' : 'block',
    }),
    input: (provided: any) => {
      return {
        ...provided,
        fontFamily: 'Roboto',
        fontWeight: 500,
        color: BLACK_COLOR,
      };
    },
    clearIndicator: (provided: any, state: any) => {
      return {
        ...provided,
        maxHeight: '25px',
        padding: '7px',
        display: state.selectProps.showClearIndicator || isTablet ? 'block' : 'none',
      };
    },
  };
};

export const getOptions = (optionsEnum: Record<string, string>): OptionType[] => {
  return [
    ...Object.values(optionsEnum).map((val) => ({
      value: val as string,
      label: val as string,
    })),
  ];
};
